import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { API, ADMIN_TOKEN_KEY } from "@/App";
import { toast } from "sonner";

const SAMPLE = {
  industries: ["Technology", "Finance", "Healthcare", "Manufacturing", "Retail", "Insurance"],
  personas: ["CEO", "CTO", "CFO", "VP Sales", "Director of Operations"],
  statuses: ["prospect", "engaged", "qualified"],
  stages: ["qualified", "discovery", "proposal", "negotiation", "closed-won"],
  companies: [
    "Acme Corp", "Global Dynamics", "InnovateTech", "DataStream Analytics", "CloudFirst Inc",
    "Enterprise Solutions", "TechVentures", "Digital Forge", "Quantum Systems", "Apex Industries",
    "Pinnacle Group", "Horizon Technologies", "Vertex Solutions", "Nexus Corp", "Catalyst Ventures",
  ],
};
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
const randInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

export const SCENARIO_RATIOS = { "crushing-it": 0.95, "behind-pace": 0.4, "mid-quarter": 0.65, "new-user": 0 };

export const useAdmin = () => {
  const [authenticated, setAuthenticated] = useState(() => !!sessionStorage.getItem(ADMIN_TOKEN_KEY));
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [platformMetrics, setPlatformMetrics] = useState(null);
  const [loading, setLoading] = useState(false);
  const selectedRef = useRef(null);

  const loadUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/users`);
      const list = res.data?.users || [];
      setUsers(list);
      const next = list.find((u) => u.userId === selectedRef.current) || list[0] || null;
      selectedRef.current = next?.userId || null;
      setSelectedUser(selectedRef.current);
      setUserProfile(next);
    } catch (error) {
      console.error("Error loading users:", error);
    }
  }, []);

  const loadMetrics = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/metrics`);
      setPlatformMetrics(res.data);
    } catch (error) {
      console.error("Error loading metrics:", error);
    }
  }, []);

  const refresh = useCallback(() => { loadUsers(); loadMetrics(); }, [loadUsers, loadMetrics]);

  useEffect(() => { if (authenticated) refresh(); }, [authenticated, refresh]);

  const login = async (username, password) => {
    try {
      const res = await axios.post(`${API}/admin/login`, { username, password });
      if (!res.data?.token) throw new Error("No token");
      sessionStorage.setItem(ADMIN_TOKEN_KEY, res.data.token);
      setAuthenticated(true);
      toast.success("Admin access granted");
      return null;
    } catch (err) {
      const msg = err.response?.data?.detail || "Invalid credentials";
      toast.error(msg);
      return msg;
    }
  };

  const logout = () => {
    sessionStorage.removeItem(ADMIN_TOKEN_KEY);
    setAuthenticated(false);
    toast.info("Logged out");
  };

  const selectUser = (userId) => {
    selectedRef.current = userId;
    setSelectedUser(userId);
    setUserProfile(users.find((u) => u.userId === userId) || null);
  };

  const run = async (fn, successMsg, errorMsg) => {
    setLoading(true);
    try {
      const result = await fn();
      if (result !== false) toast.success(typeof successMsg === "function" ? successMsg(result) : successMsg);
    } catch (error) {
      console.error(errorMsg, error);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const saveUserProfile = () => run(async () => {
    await axios.put(`${API}/users/${selectedUser}`, userProfile);
    loadUsers();
  }, "Profile updated successfully", "Failed to save profile");

  const generateSampleAccounts = () => run(async () => {
    const count = randInt(10, 20);
    for (let i = 0; i < count; i++) {
      await axios.post(`${API}/accounts`, {
        userId: selectedUser,
        companyName: `${pick(SAMPLE.companies)} ${i + 1}`,
        industry: pick(SAMPLE.industries),
        targetPersona: pick(SAMPLE.personas),
        status: pick(SAMPLE.statuses),
        engagementScore: randInt(0, 99),
        nextAction: "Initial outreach",
      });
    }
    return count;
  }, (count) => `Generated ${count} sample accounts`, "Failed to generate accounts");

  const generateSamplePipeline = () => run(async () => {
    const accounts = (await axios.get(`${API}/accounts/${selectedUser}`)).data;
    if (accounts.length === 0) {
      toast.error("Generate accounts first");
      return false;
    }
    const count = randInt(15, 25);
    for (let i = 0; i < count; i++) {
      await axios.post(`${API}/pipeline`, {
        accountId: pick(accounts).accountId,
        userId: selectedUser,
        stage: pick(SAMPLE.stages),
        value: randInt(50000, 450000),
        probability: randInt(20, 80),
      });
    }
    return count;
  }, (count) => `Generated ${count} pipeline opportunities`, "Failed to generate pipeline");

  const clearAllData = () => {
    if (!window.confirm("Are you sure you want to clear all data? This cannot be undone.")) return;
    toast.info("Data clearing not fully implemented - would clear accounts, pipeline, actions");
  };

  const triggerAgentUpdate = () => run(() => axios.post(`${API}/agent/auto-activity/${selectedUser}`), "Agent update triggered", "Failed to trigger agent");

  const loadScenario = (scenario) => {
    if (!userProfile) return;
    return run(async () => {
      await axios.put(`${API}/users/${selectedUser}`, { currentRevenue: userProfile.revenueGoal * SCENARIO_RATIOS[scenario] });
      loadUsers();
    }, `Loaded ${scenario} scenario`, "Failed to load scenario");
  };

  return {
    authenticated, users, selectedUser, userProfile, setUserProfile, platformMetrics, loading,
    login, logout, selectUser, refresh, loadUsers,
    saveUserProfile, generateSampleAccounts, generateSamplePipeline, clearAllData, triggerAgentUpdate, loadScenario,
  };
};
